import React, { useState, useMemo } from 'react';
import { SearchIcon, XMarkIcon, CheckIcon } from '../../Icons';
import { normalizeStringForSearch } from '../../../utils/formatters';

export interface OwnerOption {
  id?: string | number;
  name: string;
}

interface OwnerFilterPopoverContentProps {
  availableOwners: OwnerOption[];
  selectedOwners: string[];
  onFilterChange: (field: string, value: string[], applyImmediately?: boolean) => void;
}

export const OwnerFilterPopoverContent: React.FC<OwnerFilterPopoverContentProps> = ({
  availableOwners = [],
  selectedOwners = [],
  onFilterChange,
}) => {
  const [search, setSearch] = useState('');

  const filteredOwners = useMemo(() => {
    if (!search.trim()) return availableOwners;
    const normalized = normalizeStringForSearch(search);
    return availableOwners.filter((o) => normalizeStringForSearch(o.name).includes(normalized));
  }, [availableOwners, search]);

  const allSelected = availableOwners.length > 0 && availableOwners.every((o) => selectedOwners.includes(o.name));
  const hasSelection = selectedOwners.length > 0;

  const handleSelectAll = () => {
    const allNames = Array.from(new Set(availableOwners.map((o) => o.name).filter(Boolean)));
    onFilterChange('selectedOwner', allNames);
  };

  const handleToggleOwner = (name: string) => {
    const next = selectedOwners.includes(name)
      ? selectedOwners.filter((n) => n !== name)
      : [...selectedOwners, name];
    onFilterChange('selectedOwner', next);
  };

  return (
    <div className="space-y-3.5">
      {/* Search Bar */}
      <div className="relative">
        <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Busca inmobiliaria o fuente..."
          className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 shadow-sm"
        />
        {search && (
          <button type="button" onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 rounded-md" title="Limpiar">
            <XMarkIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Action Bar: Select All / Deselect All */}
      <div className="flex items-center justify-between gap-2 px-1 text-xs">
        <button
          type="button"
          id="owner-filter-select-all"
          onClick={handleSelectAll}
          disabled={allSelected}
          className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-xs whitespace-nowrap transition-colors ${
            allSelected
              ? 'text-gray-400 bg-gray-100 cursor-not-allowed opacity-60'
              : 'text-sky-700 bg-sky-50 hover:bg-sky-100 active:bg-sky-200 border border-sky-200/60'
          }`}
        >
          <CheckIcon className="w-3.5 h-3.5 shrink-0" />
          <span>Seleccionar todos</span>
        </button>
        {hasSelection && (
          <button
            type="button"
            id="owner-filter-deselect-all"
            onClick={() => onFilterChange('selectedOwner', [])}
            className="px-2.5 py-1.5 rounded-lg font-bold text-xs text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors whitespace-nowrap"
          >
            Deseleccionar
          </button>
        )}
      </div>

      {/* Owners List */}
      <div className="max-h-60 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
        {filteredOwners.map((o) => {
          const isChecked = selectedOwners.includes(o.name);
          return (
            <label
              key={o.id || o.name}
              className={`flex items-center p-2.5 rounded-xl cursor-pointer group transition-colors border ${
                isChecked ? 'bg-sky-50/70 border-sky-300' : 'hover:bg-gray-50 border-transparent'
              }`}
            >
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => handleToggleOwner(o.name)}
                className="rounded border-gray-300 text-sky-600 focus:ring-sky-500 mr-3 h-4 w-4"
              />
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-medium text-gray-700 truncate">{o.name}</span>
                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">FUENTE</span>
              </div>
            </label>
          );
        })}
        {filteredOwners.length === 0 && (
          <div className="py-8 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Sin resultados</p>
          </div>
        )}
      </div>

      {/* Selected Chips */}
      {hasSelection && (
        <div className="flex flex-wrap gap-1.5 pt-3 border-t border-gray-100">
          {selectedOwners.map((item) => (
            <div key={item} className="flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-bold bg-sky-50 text-sky-700 border-sky-200 shadow-sm">
              <span className="truncate max-w-[140px]">{item}</span>
              <button
                type="button"
                onClick={() => onFilterChange('selectedOwner', selectedOwners.filter((n) => n !== item))}
                className="ml-0.5 p-0.5 hover:bg-sky-200 rounded-full transition-colors text-sky-600"
                title={`Quitar ${item}`}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
